/**
 * The staff portal's first browser-side wait timing: one `POST /api/waits` per
 * owl that showed (a wait past 400 ms), so we learn which staff waits people
 * actually sit through. Server-side `responseTime` can't see the time a page
 * image spends rendering in the browser, or a wait that is several calls long.
 *
 * Only while signed in (the login page's own waits are never sent, so a 401
 * can never set off the session-expiry probe), and never an error a page
 * would notice. `AuthProvider` keeps the signed-in flag in a layout effect,
 * so it is already off when a signed-out page's owls unmount and report.
 *
 * Plan: docs/plans/2026-10-22-owl-waits-everywhere.md (staff wait timing)
 */
import type { WaitEnd } from '@ruby/owl/react';
import { apiFetch } from './api';

/** The body `POST /api/waits` takes: owl names and a duration, nothing else. */
export function waitReport(end: WaitEnd): { kind: string; act: string; elapsedMs: number; shown: true } | null {
  if (!end.shown || !end.act) return null;
  return { kind: end.kind, act: end.act, elapsedMs: Math.max(0, Math.min(600_000, Math.round(end.elapsedMs))), shown: true };
}

let signedIn = false;

/** Called by `AuthProvider` whenever the user changes. */
export function setWaitsSignedIn(on: boolean): void {
  signedIn = on;
}

export function reportWait(end: WaitEnd): void {
  const body = signedIn ? waitReport(end) : null;
  if (!body) return;
  void apiFetch('/api/waits', { method: 'POST', body: JSON.stringify(body) }).catch(() => {
    // Timing is never worth an error on screen.
  });
}
